// [S-314] RBAC custom-role hooks — Settings → Roles & permissions.
// Backed by the admin endpoints in apps/admin/src/routes/rbac.ts (S-313):
//   GET    /tenants/:id/rbac/custom-roles
//   POST   /tenants/:id/rbac/custom-roles            { name, displayName, capabilities }
//   DELETE /tenants/:id/rbac/custom-roles/:name
//   POST   /tenants/:id/rbac/assignments             { uid, roleName }
//   DELETE /tenants/:id/rbac/assignments/:uid
//
// All routes gate on the `rbac:manage` capability server-side (admin-of-
// tenant); the UI is additionally behind <RequireRole minRole="tenant_admin">.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { Capability } from "@csai/core/browser";
import { useApi } from "./api";

export type CustomRole = {
  tenantId: string;
  name: string;
  displayName: string;
  capabilities: Capability[];
  createdAt: string; // ISO
  updatedAt: string; // ISO
  createdBy: string;
};

type CustomRolesResponse = { customRoles: CustomRole[] };

export const RBAC_KEY = ["tenant", "rbac", "custom-roles"] as const;

export function useCustomRoles(
  tenantId: string
): UseQueryResult<CustomRole[], Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...RBAC_KEY, tenantId] as const,
    enabled: tenantId.length > 0,
    queryFn: async () => {
      const res = await api<CustomRolesResponse>(
        `/tenants/${encodeURIComponent(tenantId)}/rbac/custom-roles`
      );
      return res.customRoles;
    },
  });
}

export function useDefineCustomRole(): UseMutationResult<
  CustomRole,
  Error,
  {
    tenantId: string;
    name: string;
    displayName: string;
    capabilities: Capability[];
  }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, name, displayName, capabilities }) =>
      api<CustomRole>(
        `/tenants/${encodeURIComponent(tenantId)}/rbac/custom-roles`,
        { method: "POST", body: { name, displayName, capabilities } }
      ),
    onSuccess: (_data, { tenantId }) => {
      void qc.invalidateQueries({ queryKey: [...RBAC_KEY, tenantId] });
    },
  });
}

export function useDeleteCustomRole(): UseMutationResult<
  void,
  Error,
  { tenantId: string; name: string }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, name }) =>
      api<void>(
        `/tenants/${encodeURIComponent(tenantId)}/rbac/custom-roles/${encodeURIComponent(name)}`,
        { method: "DELETE" }
      ),
    onSuccess: (_data, { tenantId }) => {
      void qc.invalidateQueries({ queryKey: [...RBAC_KEY, tenantId] });
    },
  });
}

export function useAssignCustomRole(): UseMutationResult<
  void,
  Error,
  { tenantId: string; uid: string; roleName: string }
> {
  const api = useApi("admin");
  return useMutation({
    mutationFn: ({ tenantId, uid, roleName }) =>
      api<void>(
        `/tenants/${encodeURIComponent(tenantId)}/rbac/assignments`,
        { method: "POST", body: { uid, roleName } }
      ),
  });
}

export function useClearAssignedRole(): UseMutationResult<
  void,
  Error,
  { tenantId: string; uid: string }
> {
  const api = useApi("admin");
  return useMutation({
    mutationFn: ({ tenantId, uid }) =>
      api<void>(
        `/tenants/${encodeURIComponent(tenantId)}/rbac/assignments/${encodeURIComponent(uid)}`,
        { method: "DELETE" }
      ),
  });
}
